import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BackToTopButton from '../../components/feature/BackToTopButton';
import Footer from '../home/components/Footer';
import EarlyAccessHero from './components/EarlyAccessHero';
import EarlyAccessBenefits from './components/EarlyAccessBenefits';
import EarlyAccessSocialProof from './components/EarlyAccessSocialProof';
import EarlyAccessForm from './components/EarlyAccessForm';

export default function EarlyAccessPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: 'Features', to: '/features' },
    { label: 'Pricing', to: '/pricing' },
    { label: 'For CFIs', to: '/for-cfis' },
    { label: 'Safety', to: '/safety' },
  ];

  return (
    <div className="min-h-screen bg-[#060E1E] text-white font-sans">
      {/* Nav */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || menuOpen
            ? 'bg-[#0A1628]/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        } border-b border-[#00FFD1]/20`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/2de32bf8-6894-4f4a-ba90-b9480aaa88cf.png"
              alt="WingmanFix Logo"
              className="h-10 w-10 object-contain"
            />
            <span className="text-xl font-bold tracking-tight">WingmanFix</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="text-sm text-gray-300 hover:text-[#00FFD1] transition-colors uppercase tracking-wider cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}
          </div>

          <a
            href="#signup-form"
            className="hidden md:inline-flex items-center gap-2 bg-[#00FFD1] hover:bg-[#00e6bc] text-[#060E1E] font-bold px-5 py-2.5 rounded-lg transition-all text-xs uppercase tracking-wider cursor-pointer whitespace-nowrap"
          >
            <i className="ri-rocket-2-line" />
            Claim Your Spot
          </a>

          {/* Hamburger */}
          <button
            className="md:hidden flex flex-col justify-center items-center w-9 h-9 gap-1.5 cursor-pointer"
            onClick={() => setMenuOpen((p) => !p)}
            aria-label="Toggle menu"
          >
            <span className={`block h-0.5 w-6 bg-[#00FFD1] transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block h-0.5 w-6 bg-[#00FFD1] transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block h-0.5 w-6 bg-[#00FFD1] transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            menuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
          } bg-[#0A1628]/98 border-t border-[#00FFD1]/10`}
        >
          <div className="flex flex-col px-6 py-4 gap-5">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                className="text-sm text-gray-300 hover:text-[#00FFD1] transition-colors uppercase tracking-wider cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}
            <a
              href="#signup-form"
              onClick={() => setMenuOpen(false)}
              className="inline-flex items-center gap-2 bg-[#00FFD1] text-[#060E1E] font-bold px-5 py-2.5 rounded-lg text-xs uppercase tracking-wider cursor-pointer whitespace-nowrap w-fit"
            >
              <i className="ri-rocket-2-line" />
              Claim Your Spot
            </a>
          </div>
        </div>
      </nav>

      {/* Page sections */}
      <EarlyAccessHero />
      <EarlyAccessBenefits />
      <EarlyAccessSocialProof />
      <EarlyAccessForm />

      {/* Bottom urgency strip */}
      <section className="py-16 px-6 bg-[#0A1628] border-t border-[#00FFD1]/10">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full px-5 py-2 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#00FFD1] animate-pulse" />
            <span className="text-[#00FFD1] text-xs font-bold uppercase tracking-widest">
              Spots filling fast
            </span>
          </div>
          <h2 className="text-4xl font-extrabold mb-4">
            Don't miss your window.<br />
            <span className="text-[#00FFD1]">Early access closes soon.</span>
          </h2>
          <p className="text-gray-400 text-base mb-8 max-w-lg mx-auto leading-relaxed">
            We're onboarding pilots in batches. Once this wave fills up, the next invite could be months away.
          </p>
          <a
            href="#signup-form"
            className="inline-flex items-center gap-2 bg-[#00FFD1] hover:bg-[#00e6bc] text-[#060E1E] font-extrabold px-10 py-4 rounded-lg transition-all hover:scale-105 shadow-lg shadow-[#00FFD1]/20 cursor-pointer whitespace-nowrap text-sm uppercase tracking-wider"
          >
            <i className="ri-rocket-2-line" />
            Secure My Spot Now — It's Free
          </a>
          <p className="mt-4 text-xs text-gray-600">
            No credit card · No spam · Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
      <BackToTopButton />
    </div>
  );
}
